import { ThemeOptions } from '@mui/material';

// Colors

const neutral = {
  100: '#F1F2F9',
  200: '#E3E5EE',
  300: '#CFD3E2',
  400: '#A3A7BC',
  500: '#777D96',
  600: '#4D536E',
  700: '#454A61',
  800: '#323750',
  900: '#1F243C'
};

const background = {
  default: '#F3F4FF',
  paper: '#FFFFFF'
};

const divider = '#E6E8F0';
const snackbar = '#323232';

const primary = {
  main: '#005CDB',
  light: '#39BAF4',
  dark: '#0045A5',
  gradient: 'linear-gradient(to right, #005CDB, #2599EB)',
  ghost: '#EBF2FD',
  contrastText: '#FFFFFF',
  hover: '#005cdb14'
};

const secondary = {
  main: '#46CF96',
  light: '#77F2BF',
  dark: '#16A986',
  gradient: 'linear-gradient(to right, #29A688, #46CF96)',
  contrastText: '#FFFFFF'
};

const success = {
  main: '#24A879',
  light: '#89DAB8',
  dark: '#1C8156',
  contrastText: '#FFFFFF',
  gradient: 'linear-gradient(0deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), #14B8A6;',
  alert: {
    background: "#E7F8F6",
    content: "#084A42",
  },
};

const info = {
  main: '#0C82C1',
  light: '#A5D5F9',
  dark: '#146D9D',
  contrastText: '#FFFFFF',
  alert: {
    background: "#EFF8FE",
    content: "#284963",
  },
};

const warning = {
  main: '#F58711',
  light: '#FFE7AA',
  dark: '#6A3C12',
  contrastText: '#FFFFFF',
  alert: {
    background: "#FFF7E9",
    content: "#66460D",
  },
};

const error = {
  main: '#C9272A',
  light: '#FFC5C1',
  dark: '#B02E30',
  contrastText: '#FFFFFF',
  alert: {
    background: "#FAECEC",
    content: "#541B1B",
  },
};

const text = {
  primary: '#0E0E2C',
  secondary: '#6F7590',
  disabled: 'rgba(55, 65, 81, 0.48)'
};

export const lightThemeOptions: ThemeOptions = {
  components: {
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: neutral[500],
          color: '#FFFFFF'
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          '&.MuiChip-filledDefault': {
            backgroundColor: neutral[200],
            '& .MuiChip-deleteIcon': {
              color: neutral[400]
            }
          },
          '&.MuiChip-outlinedDefault': {
            '& .MuiChip-deleteIcon': {
              color: neutral[300]
            }
          }
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          '&::placeholder': {
            opacity: 1,
            color: text.secondary
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: divider
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderColor: divider,
          borderStyle: 'solid',
          borderWidth: 1
        }
      }
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderColor: divider,
          borderStyle: 'solid',
          borderWidth: 1
        }
      }
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          color: neutral[500]
        },
        track: {
          backgroundColor: neutral[400],
          opacity: 1
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${divider}`
        }
      }
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: neutral[100],
          '.MuiTableCell-root': {
            color: neutral[700]
          }
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: text.primary,
        },
        shrink: {
          color: text.primary,
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            color: text['primary'],
          }
        }
      }
    },
  },
  palette: {
    action: {
      active: neutral[500],
      focus: 'rgba(55, 65, 81, 0.12)',
      hover: 'rgba(55, 65, 81, 0.04)',
      selected: 'rgba(55, 65, 81, 0.08)',
      disabledBackground: 'rgba(55, 65, 81, 0.12)',
      disabled: 'rgba(55, 65, 81, 0.26)'
    },
    background,
    divider,
    error,
    info,
    mode: 'light',
    snackbar,
    neutral,
    primary,
    secondary,
    success,
    text,
    warning,
  },
  shadows: [
    'none',
    '0px 1px 1px rgba(100, 116, 139, 0.06), 0px 1px 2px rgba(100, 116, 139, 0.1)',
    '0px 1px 2px rgba(100, 116, 139, 0.12)',
    '0px 1px 4px rgba(100, 116, 139, 0.12)',
    '0px 1px 5px rgba(100, 116, 139, 0.12)',
    '0px 1px 6px rgba(100, 116, 139, 0.12)',
    '0px 2px 6px rgba(100, 116, 139, 0.12)',
    '0px 3px 6px rgba(100, 116, 139, 0.12)',
    '0px 2px 4px rgba(31, 41, 55, 0.06), 0px 4px 6px rgba(100, 116, 139, 0.12)',
    '0px 5px 12px rgba(100, 116, 139, 0.12)',
    '0px 5px 14px rgba(100, 116, 139, 0.12)',
    '0px 5px 15px rgba(100, 116, 139, 0.12)',
    '0px 6px 15px rgba(100, 116, 139, 0.12)',
    '0px 7px 15px rgba(100, 116, 139, 0.12)',
    '0px 8px 15px rgba(100, 116, 139, 0.12)',
    '0px 9px 15px rgba(100, 116, 139, 0.12)',
    '0px 10px 15px rgba(100, 116, 139, 0.12)',
    '0px 12px 22px -8px rgba(100, 116, 139, 0.25)',
    '0px 13px 22px -8px rgba(100, 116, 139, 0.25)',
    '0px 14px 24px -8px rgba(100, 116, 139, 0.25)',
    '0px 10px 10px rgba(31, 41, 55, 0.04), 0px 20px 25px rgba(31, 41, 55, 0.1)',
    '0px 25px 50px rgba(100, 116, 139, 0.25)',
    '0px 25px 50px rgba(100, 116, 139, 0.25)',
    '0px 25px 50px rgba(100, 116, 139, 0.25)',
    '0px 25px 50px rgba(100, 116, 139, 0.25)'
  ]
};
